import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { DailyHistorySortKey } from '@/features/shared/components/EntityDailyHistoryPanel.types';
import type { QueryFilter, QuerySort, SuiteExecutionStatus } from '@flowqa/shared';

type BuildSuiteHistoryQueryStateKeyInput = {
  cloneDateQuickFilter: string;
  cloneDateWindowEndDay: string | null;
  cloneDateWindowPreviousEndDay: string | null;
  cloneDateWindowPreviousStartDay: string | null;
  cloneDateWindowStartDay: string | null;
  cloneSearchValue: string;
  cloneFilterStatuses: SuiteExecutionStatus[];
  cloneFilterSuites: string[];
  cloneFilterTags: string[];
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyPage: number;
  historyPageSize: number;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

type BuildSuiteHistoryQueryInputArgs = {
  cloneDateWindowEndDay: string | null;
  cloneDateWindowPreviousEndDay: string | null;
  cloneDateWindowPreviousStartDay: string | null;
  cloneDateWindowStartDay: string | null;
  cloneSearchValue: string;
  cloneFilterStatuses: SuiteExecutionStatus[];
  cloneFilterSuites: string[];
  cloneFilterTags: string[];
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyPage: number;
  historyPageSize: number;
  showCloneDeltas: boolean;
};

type SuiteHistoryQueryInput = {
  page: number;
  pageSize: number;
  sorts: QuerySort[];
  filters: QueryFilter[];
  search?: string;
  metricsStartDay?: string;
  metricsEndDay?: string;
  previousMetricsStartDay?: string;
  previousMetricsEndDay?: string;
  include: {
    project: true;
    latestExecution: true;
    executionStatusCounts: true;
    dailyHistory: true;
    executionSummaries: true;
    latestTestResults: { counts: true; summaries: true };
    metrics: true;
    metricDeltas: boolean;
  };
};

export const buildSuiteHistoryQueryStateKey = (
  input: BuildSuiteHistoryQueryStateKeyInput,
): string =>
  JSON.stringify({
    cloneDateQuickFilter: input.cloneDateQuickFilter,
    cloneSearchValue: input.cloneSearchValue,
    cloneFilterStatuses: input.cloneFilterStatuses,
    cloneFilterSuites: input.cloneFilterSuites,
    cloneFilterTags: input.cloneFilterTags,
    historySortDirection: input.historySortDirection,
    historySortKey: input.historySortKey,
    cloneDateWindowStartDay: input.cloneDateWindowStartDay,
    cloneDateWindowEndDay: input.cloneDateWindowEndDay,
    cloneDateWindowPreviousStartDay: input.cloneDateWindowPreviousStartDay,
    cloneDateWindowPreviousEndDay: input.cloneDateWindowPreviousEndDay,
    historyPage: input.historyPage,
    historyPageSize: input.historyPageSize,
    selectedOrganisationId: input.selectedOrganisationId,
    selectedProjectId: input.selectedProjectId,
  });

const buildSuiteHistoryFilters = (
  cloneFilterStatuses: SuiteExecutionStatus[],
  cloneFilterSuites: string[],
  cloneFilterTags: string[],
): QueryFilter[] =>
  mergeFilters(
    buildInFilter('latestExecutionStatus', cloneFilterStatuses),
    buildInFilter('id', cloneFilterSuites),
    buildInFilter('tags', cloneFilterTags),
  );

export const buildSuiteHistoryQueryInput = (
  args: BuildSuiteHistoryQueryInputArgs,
): SuiteHistoryQueryInput => ({
  page: args.historyPage + 1,
  pageSize: args.historyPageSize,
  search: args.cloneSearchValue.trim(),
  sorts: [{ field: args.historySortKey, direction: args.historySortDirection }],
  filters: buildSuiteHistoryFilters(
    args.cloneFilterStatuses,
    args.cloneFilterSuites,
    args.cloneFilterTags,
  ),
  metricsStartDay: args.cloneDateWindowStartDay ?? undefined,
  metricsEndDay: args.cloneDateWindowEndDay ?? undefined,
  previousMetricsStartDay: args.cloneDateWindowPreviousStartDay ?? undefined,
  previousMetricsEndDay: args.cloneDateWindowPreviousEndDay ?? undefined,
  include: {
    project: true,
    latestExecution: true,
    executionStatusCounts: true,
    dailyHistory: true,
    executionSummaries: true,
    latestTestResults: { counts: true, summaries: true },
    metrics: true,
    metricDeltas: args.showCloneDeltas,
  },
});
